import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Portal,
  useMediaQuery,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHasBottomBar } from "../layout/pageFrame";
import type { WizardStep } from "./wizardSteps";
import { WizardStepList } from "./WizardStepList";

const BAR_HEIGHT_PX = 60;

interface Props<K extends string> {
  steps: ReadonlyArray<WizardStep<K>>;
  currentStep: K;
  navigateToStep: (step: K) => void;
  stepLabel: (step: K) => string;
}

export const MobileStepDrawer = <K extends string>({ steps, currentStep, navigateToStep, stepLabel }: Props<K>) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");

  const closeAndNavigate = (step: K) => {
    setShowDrawer(false);
    navigateToStep(step);
  };

  if (isLargerThan768) return null;

  return (
    <>
      <Drawer placement="bottom" onClose={() => setShowDrawer(false)} isOpen={showDrawer}>
        <DrawerOverlay />
        <DrawerContent borderTop="1px solid" borderColor="smBorder">
          <DrawerBody py="4">
            <WizardStepList
              steps={steps}
              currentStep={currentStep}
              navigateToStep={closeAndNavigate}
              stepLabel={stepLabel}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <ChangeStepBar onOpen={() => setShowDrawer(true)} />
    </>
  );
};

const ChangeStepBar = ({ onOpen }: { onOpen: () => void }) => {
  const { t } = useTranslation();
  useHasBottomBar({ heightPx: BAR_HEIGHT_PX });

  return (
    <Portal>
      <Flex
        position="fixed"
        zIndex={20}
        bottom="0"
        left="0"
        right="0"
        height={`${BAR_HEIGHT_PX}px`}
        borderTop="1px solid"
        borderColor="smBorder"
        alignItems="center"
        bg="smBackground"
        px="3"
      >
        <Button variant="solid" colorScheme="teal" width="full" onClick={onOpen}>
          {t("Change step")}
        </Button>
      </Flex>
    </Portal>
  );
};
